import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete, Save } from '@mui/icons-material';
import api from '../../services/api';
import { formatMoney } from '../commercial/contact';
import { todayIso } from './ExpenseDialog';

const STATUS_CHIP: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'default' }> = {
  ok: { label: 'OK', color: 'success' },
  'al-limite': { label: 'Al límite', color: 'warning' },
  excedido: { label: 'Excedido', color: 'error' },
  'sin-presupuesto': { label: 'Sin presupuesto', color: 'default' },
};

/**
 * Presupuesto mensual por categoría (se repite todos los meses) y el real del mes elegido contra ese presupuesto
 * y contra el tope de gastos sobre la facturación.
 */
const BudgetPanel: React.FC<{ categories: string[] }> = ({ categories }) => {
  const [budgets, setBudgets] = useState<{ category: string; monthlyAmount: number }[]>([]);
  const [month, setMonth] = useState(todayIso().slice(0, 7));
  const [report, setReport] = useState<any>(null);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.getBudgets().then(setBudgets).catch(() => setBudgets([])); }, []);

  const loadReport = useCallback(async () => {
    try { setReport(await api.getExpenseBudget(month)); } catch { setReport(null); }
  }, [month]);
  useEffect(() => { loadReport(); }, [loadReport]);

  const save = async () => {
    setSaving(true);
    try {
      await api.setBudgets(budgets.filter(b => b.category.trim() && b.monthlyAmount > 0));
      setMsg({ type: 'success', text: 'Presupuesto guardado' });
      loadReport();
    } catch (e: any) {
      setMsg({ type: 'error', text: e?.response?.data?.message || 'No se pudo guardar el presupuesto' });
    } finally {
      setSaving(false);
    }
  };

  const used = new Set(budgets.map(b => b.category.toLowerCase()));
  const addRow = () => {
    const next = categories.find(c => !used.has(c.toLowerCase())) || '';
    setBudgets(b => [...b, { category: next, monthlyAmount: 0 }]);
  };

  const ratioPct = report ? Math.round(report.expenseRatio * 1000) / 10 : 0;
  const limitPct = Math.round((report?.expenseRatioLimit ?? 0.5) * 100);
  const total = budgets.reduce((s, b) => s + (b.monthlyAmount || 0), 0);

  return (
    <Stack spacing={2}>
      {msg && <Alert severity={msg.type} onClose={() => setMsg(null)}>{msg.text}</Alert>}
      <Card variant="outlined">
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} sx={{ mb: 1 }}>
            <Box>
              <Typography variant="h6" fontWeight={700}>Presupuesto mensual por categoría</Typography>
              <Typography variant="body2" color="text.secondary">
                Cuánto querés gastar por mes en cada categoría. Se usa el mismo todos los meses. Total: <b>{formatMoney(total)}</b>
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button startIcon={<Add />} onClick={addRow}>Agregar categoría</Button>
              <Button variant="contained" startIcon={<Save />} onClick={save} disabled={saving}>Guardar</Button>
            </Stack>
          </Stack>
          <Stack spacing={1}>
            {budgets.map((b, i) => {
              const options = b.category && !categories.includes(b.category) ? [...categories, b.category] : categories;
              return (
                <Stack key={i} direction="row" spacing={1} alignItems="center">
                  <TextField select size="small" label="Categoría" value={b.category} sx={{ flex: 1 }}
                    onChange={e => setBudgets(x => x.map((y, idx) => (idx === i ? { ...y, category: e.target.value } : y)))}>
                    {options.map(c => (
                      <MenuItem key={c} value={c} disabled={c !== b.category && used.has(c.toLowerCase())}>{c}</MenuItem>
                    ))}
                  </TextField>
                  <TextField size="small" type="number" label="Monto por mes" value={b.monthlyAmount || ''} sx={{ width: 170 }}
                    onChange={e => setBudgets(x => x.map((y, idx) => (idx === i ? { ...y, monthlyAmount: Number(e.target.value) } : y)))} />
                  <IconButton onClick={() => setBudgets(x => x.filter((_, idx) => idx !== i))} aria-label="Quitar"><Delete /></IconButton>
                </Stack>
              );
            })}
            {budgets.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Todavía no hay presupuesto. Tocá "Agregar categoría", elegí la categoría, poné el monto por mes y Guardar.
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Stack direction="row" spacing={2} alignItems="center">
        <TextField size="small" type="month" label="Mes" value={month} onChange={e => setMonth(e.target.value)} InputLabelProps={{ shrink: true }} />
      </Stack>
      {report && (
        <Card variant="outlined">
          <CardContent>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
              <Box>
                <Typography variant="body2" color="text.secondary">Gastos sobre facturación</Typography>
                <Typography variant="h4" fontWeight={800} color={report.overLimit ? 'error.main' : 'success.main'}>{ratioPct}%</Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatMoney(report.totalActual)} de gastos · {formatMoney(report.revenue)} facturado · tope {limitPct}%
                </Typography>
              </Box>
              {report.overLimit && <Alert severity="error" sx={{ alignSelf: 'center' }}>Los gastos superan el {limitPct}% de lo facturado.</Alert>}
            </Stack>
            <LinearProgress variant="determinate" value={Math.min(100, limitPct ? (ratioPct / limitPct) * 100 : 0)}
              color={report.overLimit ? 'error' : 'success'} sx={{ mt: 2, height: 8, borderRadius: 4 }} />
            <Table size="small" sx={{ mt: 2 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Categoría</TableCell>
                  <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Fijos</TableCell>
                  <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Día a día</TableCell>
                  <TableCell align="right">Real</TableCell>
                  <TableCell align="right">Presupuesto</TableCell>
                  <TableCell align="right">Estado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report.rows.map((r: any) => (
                  <TableRow key={r.category}>
                    <TableCell>{r.category}</TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{formatMoney(r.fixed)}</TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{formatMoney(r.variable)}</TableCell>
                    <TableCell align="right"><b>{formatMoney(r.actual)}</b></TableCell>
                    <TableCell align="right">{r.budget ? formatMoney(r.budget) : '—'}</TableCell>
                    <TableCell align="right"><Chip size="small" {...STATUS_CHIP[r.status]} /></TableCell>
                  </TableRow>
                ))}
                {report.rows.length === 0 && (
                  <TableRow><TableCell colSpan={6}><Typography variant="body2" color="text.secondary">Sin gastos ni presupuesto en este mes.</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Fijos = gastos fijos activos. Día a día = gastos cargados en el mes. El tope de gastos se cambia en Costos y objetivos.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
};

export default BudgetPanel;
